const SAML_KEY = '_saml_';
const SAML_SESSION_DURATION = 30 * 60 * 1000; // 30 minutes

/**
 * Store SAML session timestamp. Called on initial SAML login and on each
 * session resume (page load, tab focus) to implement a sliding window TTL.
 * The 30-min clock resets on every call, so active users stay logged in.
 */
export function storeSamlSession(uid: string): void {
  localStorage.setItem(SAML_KEY, JSON.stringify({ t: uid, w: Date.now() }));
}

/** Clear SAML session (on logout or expired). */
export function clearSamlSession(): void {
  localStorage.removeItem(SAML_KEY);
}

/**
 * Validate SAML session TTL.
 * - 'not-saml': no SAML session stored, or uid mismatch (different login method)
 * - 'expired': SAML session exists for this uid but exceeded 30-minute TTL
 * - 'valid': SAML session exists and still within TTL
 */
export function validateSamlSession(uid: string): 'valid' | 'expired' | 'not-saml' {
  const raw = localStorage.getItem(SAML_KEY);
  if (!raw) return 'not-saml';

  try {
    const saml = JSON.parse(raw) as { t: string; w: number };
    if (saml.t !== uid) return 'not-saml';
    if (saml.w + SAML_SESSION_DURATION < Date.now()) return 'expired';
    return 'valid';
  } catch {
    return 'not-saml';
  }
}

/**
 * Check SAML session and enforce TTL with sliding window.
 * Returns true if session is expired (caller should handle logout).
 * Returns false if valid (refreshes timestamp) or not a SAML session.
 */
export function checkSamlSessionExpired(uid: string): boolean {
  const status = validateSamlSession(uid);
  if (status === 'expired') {
    return true;
  }
  if (status === 'valid') {
    storeSamlSession(uid);
  }
  return false;
}
